import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/privacy-policy')({
  component: PrivacyPolicyComponent,
  head: () => ({
    meta: [
      {
        title: 'Privacy Policy - HAMD',
      },
    ],
  }),
  beforeLoad: () => ({
    title: 'Privacy Policy',
    navbar: {
      showTitle: true,
    },
  }),
});

function PrivacyPolicyComponent() {
  return (
    <main className="max-w-4xl mx-auto px-4 py-8 pb-20">
      <div className="space-y-8">
        <section>
          <h1 className="text-h1 font-bold mb-4">Privacy Policy</h1>
          <p className="text-small text-dark-gray">
            <strong>Last Update:</strong> February 2, 2026
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 font-semibold">1. Introduction</h2>
          <p className="text-small text-dark">
            Welcome to HAMD ("we," "our," or "us"). HAMD is a mental health and wellbeing application 
            designed to provide psychoeducational content, interactive therapeutic exercises, and 
            self-help tools for individuals dealing with stress, fear, trauma, and other mental health challenges.
          </p>
          <p className="text-small text-dark">
            We are committed to protecting your privacy and personal information. This Privacy Policy 
            explains how we collect, use, disclose, and safeguard your information when you use our 
            mobile application and web services.
          </p>
          <p className="text-small text-dark">
            By using HAMD, you agree to the collection and use of information in accordance with this policy.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 font-semibold">2. Information We Collect</h2>
          
          <div className="space-y-3">
            <h3 className="text-h3 font-medium">2.1 Information You Provide</h3>
            <p className="text-small text-dark">We collect information that you voluntarily provide when using HAMD:</p>
            <ul className="list-disc list-inside space-y-2 text-small text-dark ml-4">
              <li><strong>Account Information:</strong> Nickname (username), gender, and password (stored as a bcrypt hash)</li>
              <li><strong>Therapeutic Content:</strong> Diary entries, feelings, reflections, and responses to therapeutic exercises</li>
              <li><strong>Session Data:</strong> Information from your participation in exercises including:
                <ul className="list-circle list-inside ml-6 mt-1 space-y-1">
                  <li>Personal source responses</li>
                  <li>Social network connections and reflections</li>
                  <li>Positive imagery selections and associated feelings</li>
                  <li>Happy place drawings and artwork</li>
                  <li>Tree of life session data</li>
                  <li>Strengths mapping information</li>
                  <li>Conversing Together reflections</li>
                </ul>
              </li>
              <li><strong>Client Information (Optional):</strong> Name, age, location, preferred therapist gender, language, preferred contact method, and (if provided) phone and/or email</li>
              <li><strong>Contact Information (Optional):</strong> Email address or phone number if you choose to share it with us for support or communications</li>
              <li><strong>Information About Other People (If You Provide It):</strong> Names and reflections you enter in the “Social Network” feature</li>
              <li><strong>Preferences:</strong> Language preferences, audience type (adult/child), and other customization settings</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className="text-h3 font-medium">2.2 Automatically Collected Information</h3>
            <p className="text-small text-dark">When you use HAMD, we automatically collect certain information:</p>
            <ul className="list-disc list-inside space-y-2 text-small text-dark ml-4">
              <li><strong>Usage Data:</strong> Information about how you interact with the app, including exercises completed, session durations, and feature usage</li>
              <li><strong>Device and Browser Information:</strong> Basic device and browser details (e.g., operating system and browser type) that may be available through standard logs</li>
              <li><strong>Log Data:</strong> Diagnostic and security-related log data that may include access times and request metadata</li>
              <li><strong>Timestamps:</strong> Creation and update times for all content you generate</li>
            </ul>
            <p className="text-small text-dark mt-2">
              We do not intentionally collect sensitive device data (such as precise location) unless you explicitly provide it.
            </p>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 font-semibold">3. How We Use Your Information</h2>
          <p className="text-small text-dark">We use the collected information for the following purposes:</p>
          <ul className="list-disc list-inside space-y-2 text-small text-dark ml-4">
            <li><strong>Service Delivery:</strong> To provide, maintain, and improve our therapeutic exercises and features</li>
            <li><strong>Personalization:</strong> To customize content and recommendations based on your age group and needs</li>
            <li><strong>Progress Tracking:</strong> To help you track your therapeutic journey and completed exercises</li>
            <li><strong>Account Management:</strong> To manage your account, authenticate access, and provide user support</li>
            <li><strong>Communication:</strong> To send you notifications about your account or app features (if you've provided contact information)</li>
            <li><strong>Analytics:</strong> To understand how users interact with our app and improve user experience</li>
            <li><strong>Security:</strong> To detect, prevent, and address technical issues and fraudulent activity</li>
            <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 font-semibold">4. Data Storage and Security</h2>
          <p className="text-small text-dark">
            We store your information on our servers (and, for some media like drawings, in cloud storage). We use reasonable safeguards designed to protect your data.
          </p>
          <ul className="list-disc list-inside space-y-2 text-small text-dark ml-4">
            <li><strong>Passwords:</strong> Stored as hashed values (not plain text)</li>
            <li><strong>Secure access:</strong> Authentication and access controls help protect your account</li>
            <li><strong>Secure transmission:</strong> We aim to protect data in transit (for example, via HTTPS)</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 font-semibold">5. Data Sharing and Disclosure</h2>
          <p className="text-small text-dark">
            We do not sell, trade, or rent your personal information to third parties. We may share your 
            information only in the following limited circumstances:
          </p>
          <ul className="list-disc list-inside space-y-2 text-small text-dark ml-4">
            <li><strong>With Your Consent:</strong> When you explicitly authorize us to share specific information</li>
            <li><strong>Service Providers:</strong> With trusted third-party service providers who assist us in operating 
            our application (e.g., cloud hosting providers, email services), under strict confidentiality agreements</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority, or to 
            protect our rights, property, or safety</li>
            <li><strong>Emergency Situations:</strong> If we believe disclosure is necessary to protect against imminent 
            harm to the safety of any person</li>
            <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, or acquisition, 
            your information may be transferred (you will be notified via prominent notice)</li>
          </ul>
          <p className="text-small text-dark font-medium mt-3">
            We do not share your therapeutic content, diary entries, or personal reflections with any third parties 
            except as explicitly stated above.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 font-semibold">6. Your Rights and Choices</h2>
          <p className="text-small text-dark">You have the following rights regarding your personal information:</p>
          
          <div className="space-y-3">
            <h3 className="text-h3 font-medium">6.1 Access and Portability</h3>
            <p className="text-small text-dark">
              You have the right to access and obtain a copy of your personal data stored in HAMD.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="text-h3 font-medium">6.2 Correction</h3>
            <p className="text-small text-dark">
              You can update or correct your account information and therapeutic content at any time through the app.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="text-h3 font-medium">6.3 Deletion</h3>
            <p className="text-small text-dark">
              You may be able to delete certain items within the app (for example, specific session data where the feature supports it). You can also request deletion of your account and associated data by contacting us.
            </p>
            <ul className="list-disc list-inside space-y-2 text-small text-dark ml-4">
              <li>We will verify your request to help prevent unauthorized deletion</li>
              <li>We will delete or anonymize your personal data where reasonably possible</li>
              <li>We may retain limited information if required for legal, security, or fraud-prevention purposes</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className="text-h3 font-medium">6.4 Objection and Restriction</h3>
            <p className="text-small text-dark">
              You can object to certain data processing activities or request restriction of processing 
              in specific circumstances.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="text-h3 font-medium">6.5 Data Retention</h3>
            <p className="text-small text-dark">
              We retain your personal information for as long as your account is active or as needed to provide 
              services. If you request account deletion, we will process it within a reasonable time, except where we are 
              required to retain certain information for legal, security, or compliance purposes.
            </p>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 font-semibold">7. Children's Privacy</h2>
          <p className="text-small text-dark">
            HAMD provides age-appropriate content for children (ages 3-18) and adults. We take special care with 
            children's data:
          </p>
          <ul className="list-disc list-inside space-y-2 text-small text-dark ml-4">
            <li>We recommend parental supervision for users under 13</li>
            <li>We collect only necessary information for providing therapeutic services</li>
            <li>We do not knowingly collect more information from children than is necessary</li>
            <li>Parents/guardians can contact us to access, modify, or delete their child's information</li>
          </ul>
          <p className="text-small text-dark mt-2">
            If you believe we have inadvertently collected information from a child without proper consent, 
            please contact us immediately.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 font-semibold">8. International Data Transfers</h2>
          <p className="text-small text-dark">
            Your information may be transferred to and maintained on servers located outside of your country, 
            where data protection laws may differ. We ensure appropriate safeguards are in place to protect 
            your information in accordance with this Privacy Policy.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 font-semibold">9. Third-Party Services</h2>
          <p className="text-small text-dark">
            HAMD uses the following third-party services:
          </p>
          <ul className="list-disc list-inside space-y-2 text-small text-dark ml-4">
            <li><strong>Amazon Web Services (AWS):</strong> For cloud storage of media files</li>
            <li><strong>Email Services:</strong> For account notifications (if provided)</li>
          </ul>
          <p className="text-small text-dark mt-2">
            These services have their own privacy policies. We encourage you to review them. We are not 
            responsible for the privacy practices of third-party services.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 font-semibold">10. Cookies and Tracking Technologies</h2>
          <p className="text-small text-dark">
            HAMD may use essential browser storage (for example, local storage) and caching (for example, service-worker caching) to support core functionality and improve performance.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 font-semibold">10.1 Sensitive Information</h2>
          <p className="text-small text-dark">
            Because HAMD involves journaling and wellbeing exercises, some information you enter (such as diary entries, feelings, reflections, and exercise responses) may be considered sensitive personal data in some jurisdictions. We recommend that you do not include information you do not want stored in your account.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 font-semibold">11. Important Disclaimers</h2>
          
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 space-y-2">
            <h3 className="text-h3 font-semibold text-amber-900">Medical Disclaimer</h3>
            <p className="text-small text-amber-900">
              HAMD is designed for psychoeducational and self-help purposes only. It is not a substitute for 
              professional medical advice, diagnosis, or treatment. If you are experiencing a mental health crisis 
              or emergency, please contact local emergency services or a mental health professional immediately.
            </p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 space-y-2 mt-3">
            <h3 className="text-h3 font-semibold text-blue-900">No Therapist-Patient Relationship</h3>
            <p className="text-small text-blue-900">
              Use of HAMD does not create a therapist-patient relationship. The app provides self-guided exercises 
              and educational content but does not constitute professional therapy or counseling.
            </p>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 font-semibold">12. Changes to This Privacy Policy</h2>
          <p className="text-small text-dark">
            We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, 
            operational, or regulatory reasons. We will notify you of any material changes by:
          </p>
          <ul className="list-disc list-inside space-y-2 text-small text-dark ml-4">
            <li>Posting the new Privacy Policy in the app</li>
            <li>Updating the "Last Updated" date</li>
            <li>Sending a notification if you've provided contact information</li>
          </ul>
          <p className="text-small text-dark mt-2">
            Your continued use of HAMD after changes are posted constitutes acceptance of the updated Privacy Policy.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 font-semibold">13. Contact Us</h2>
          <p className="text-small text-dark">
            If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
            please contact us:
          </p>
          <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-small text-dark">
            <p><strong>Email:</strong> contact@hamd.life</p>
            <p><strong>Address:</strong> Westerholter Weg 19, 45657 Recklinghausen</p>
            <p><strong>Response Time:</strong> We aim to respond to all inquiries within 30 days</p>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 font-semibold">14. Legal Basis for Processing (GDPR)</h2>
          <p className="text-small text-dark">
            If you are in the European Economic Area (EEA), our legal basis for collecting and using your 
            personal information depends on the specific context:
          </p>
          <ul className="list-disc list-inside space-y-2 text-small text-dark ml-4">
            <li><strong>Consent:</strong> You have given explicit consent for specific processing activities</li>
            <li><strong>Contract:</strong> Processing is necessary to provide services you've requested</li>
            <li><strong>Legal Obligation:</strong> Processing is required by law</li>
            <li><strong>Legitimate Interests:</strong> Processing is in our legitimate interests and not overridden by your rights</li>
          </ul>
        </section>

        <section className="border-t pt-6 mt-8">
          <p className="text-small text-dark-gray italic">
            By using HAMD, you acknowledge that you have read and understood this Privacy Policy and agree 
            to its terms. If you do not agree with this policy, please do not use our services.
          </p>
          <p className="text-small text-dark-gray italic mt-4">
            Thank you for trusting HAMD with your mental health journey. We are committed to protecting your 
            privacy and providing a safe, secure environment for your therapeutic exercises and personal growth.
          </p>
        </section>
      </div>
    </main>
  );
}
